import { LAYOUT_DEFAULTS } from '../types/layoutDefaults'
import type { Layout } from '../types/layout'
import type { Orientation, ThemeConfig, TranscriptionStyle } from '../types'
import type {
  TimelineItemV3,
  TrackV3,
  Transform,
} from '../types/renderConfigV2'
import applyTheme from '../utils/applyTheme'
import { FONTS } from '../utils/fonts'
import { orientationOf } from './refit'
import {
  BLUEPRINTS,
  type BlueprintItem,
  type StyleRole,
} from './templates/index'

export * from './templates/index'

/**
 * The template engine (#77): a layout's blueprint instantiated for one
 * canvas, theme and episode, as persisted generation-3 items on one track
 * each.
 * The converter runs it to open a v1 render as a project; the editor runs
 * it when a template is picked or the orientation changes — so "Edit on a
 * v1 render" and "apply template" produce the same items.
 *
 * Item ids are `<blueprint id>-1`, which is exactly what the converter wrote
 * before blueprints existed (`background-1`, `visualizer-1`, `title-1`,
 * `subtitle-1`, `captions-1`), so projects saved by it still count as
 * template items. A FOLLOWER (captions, the speaker's name, handle and
 * avatar, the visualizer) belongs to a scene (#541), so the layout places one
 * per scene it is given: `<blueprint id>-<n>` for the n-th scene, naming it
 * in `follows`, spanning it. A one-scene document therefore keeps the ids
 * above. The scenes sit on one track and never overlap, so a follower's
 * copies share one track. Every other item spans `span` once and, when the
 * layout is applied for exactly one scene, names it in `scene` (membership).
 */

/** A scene the layout places followers for: its id and output span, seconds. */
export type TemplateScene = { id: string; from: number; duration: number }

export type TemplateEpisode = {
  title?: string | null
  subtitle?: string | null
  /** The host picture (`clipScene.imageUrl`, the speaker avatar). */
  hostImageUrl?: string | null
  speakerName?: string | null
  speakerHandle?: string | null
}

export const REPORT_NO_HOST_IMAGE =
  'No host image is set, so the template left its host picture out'
export const DEFAULT_TEMPLATE_FONT = 'Red Hat Display'

function knownFont(family: string | undefined): family is string {
  return !!family && FONTS.some((f) => f.family === family)
}

export function templateItems({
  layout,
  width,
  height,
  theme,
  episode,
  mediaKind,
  span,
  scenes,
}: {
  layout: Layout
  width: number
  height: number
  theme: ThemeConfig
  episode: TemplateEpisode
  /** What the media track plays: a video needs no background or visualizer. */
  mediaKind: 'audio' | 'video'
  /** The seconds every non-follower spans: the media track's scenes together. */
  span: { from: number; duration: number }
  /** The scenes followers are placed for, one copy each; none before the first scene. */
  scenes: readonly TemplateScene[]
}): { tracks: TrackV3[]; report: string[] } {
  const report: string[] = []
  const orientation = orientationOf({ width, height })
  const defaults = LAYOUT_DEFAULTS[layout]
  const style = applyTheme(defaults.defaultStyle, theme)
  const blueprint = BLUEPRINTS[layout].orientations[orientation]

  const fontFamily = knownFont(theme.fonts.primary)
    ? theme.fonts.primary
    : DEFAULT_TEMPLATE_FONT
  if (fontFamily !== theme.fonts.primary) {
    report.push(
      `Font "${theme.fonts.primary}" is not available; using ${DEFAULT_TEMPLATE_FONT}`,
    )
  }
  // A layout that pins its caption face (meme's Anton) keeps it when the
  // catalogue knows it; one that inherits the theme font gets the theme font.
  const pinnedCaptionFont = defaults.defaultStyle.transcription.fontFamily
  const captionFontFamily =
    pinnedCaptionFont !== 'primary' && knownFont(pinnedCaptionFont)
      ? pinnedCaptionFont
      : fontFamily

  const role = (r: StyleRole): string => {
    switch (r) {
      case 'background':
        return style.background.color ?? theme.colors.primary
      case 'border':
        return style.border.color
      case 'title':
        return style.title.color
      case 'subtitle':
        return style.subtitle.color
      case 'waveform':
        return style.waveform.color
      case 'logo':
        return style.logo.color
      case 'speakerName':
        return (style.speakerName ?? style.speakerUsername).color
      case 'speakerUsername':
        return style.speakerUsername.color
      case 'white':
        return '#ffffff'
      case 'transparent':
        return 'transparent'
      default:
        return r
    }
  }

  // A theme whose colours agree would hide an item on its surface (the page,
  // or the card a layout draws it on): give it the next theme colour that is
  // not the surface. Reported, since the look changes. A video has no page
  // colour, so nothing is compared.
  const surfaceOf = (item: BlueprintItem): string | null => {
    if (mediaKind === 'video') return null
    return role(item.surface === 'border' ? 'border' : 'background')
  }
  const visible = (color: string, surface: string | null, what: string) => {
    if (surface === null || color !== surface) return color
    const candidates = [
      style.border.color,
      theme.colors.secondary,
      theme.colors.primary,
      theme.colors.accent,
    ]
    const picked = candidates.find((c) => c && c !== surface) ?? color
    if (picked !== color) {
      report.push(
        `The ${what} was recoloured: its colour matched the surface it sits on`,
      )
    }
    return picked
  }

  const transformOf = (
    item: BlueprintItem,
    extra: Partial<Transform> = {},
  ): Transform => ({
    x: Math.round(item.box.x * width),
    y: Math.round(item.box.y * height),
    width: Math.max(1, Math.round(item.box.w * width)),
    height: Math.max(1, Math.round(item.box.h * height)),
    ...extra,
  })
  const opacityOf = (opacity: number | undefined) =>
    opacity !== undefined && opacity !== 1 ? { opacity } : {}

  const ordered = [...scenes].sort((a, b) => a.from - b.from)
  const owner = ordered.length === 1 ? { scene: ordered[0]!.id } : {}
  const timing = { from: span.from, duration: span.duration, ...owner }
  /** One copy per scene, spanning it and bound to it. */
  const perScene = (
    make: (timing: PerSceneTiming, n: number) => TimelineItemV3,
  ) =>
    ordered.map((scene, index) =>
      make(
        { from: scene.from, duration: scene.duration, follows: scene.id },
        index + 1,
      ),
    )
  const built: Array<{ z: number; items: TimelineItemV3[] }> = []

  for (const item of blueprint) {
    if (
      mediaKind === 'video' &&
      (item.id === 'background' || item.id === 'visualizer')
    )
      continue
    const id = `${item.id}-1`
    const surface = surfaceOf(item)
    switch (item.type) {
      case 'solid':
        built.push({
          z: item.z,
          items: [
            {
              id,
              type: 'solid',
              ...timing,
              color: role(item.color),
              ...(item.stroke
                ? {
                    stroke: {
                      color: role(item.stroke.color),
                      width: Math.max(1, Math.round(item.stroke.width * width)),
                    },
                  }
                : {}),
              transform: transformOf(item, {
                ...opacityOf(item.opacity),
                ...(item.borderRadius
                  ? { borderRadius: Math.round(item.borderRadius * width) }
                  : {}),
              }),
            },
          ],
        })
        break
      case 'text': {
        const dynamicField =
          item.text === 'speakerName'
            ? 'name'
            : item.text === 'speakerHandle'
              ? 'handle'
              : null
        // A static text with nothing to say is left out; a dynamic one stays
        // with no words of its own, since the transcript fills it.
        const text = dynamicField
          ? ''
          : item.text === 'literal'
            ? item.literal
            : episode[item.text]
        if (!dynamicField && !text) break
        const fontSize = Math.max(1, Math.round(item.fontSize * width))
        const box = transformOf(item)
        // Clamped to the lines its box holds, as v1's `line-clamp-N` did: a
        // longer title is cut, not spilled over what sits below. A glyph has
        // no lines to clamp.
        const maxLines =
          item.text === 'literal'
            ? undefined
            : Math.max(1, Math.round(box.height / (fontSize * item.lineHeight)))
        const style = {
          fontFamily,
          fontWeight: item.fontWeight,
          ...(item.fontStyle ? { fontStyle: item.fontStyle } : {}),
          fontSize,
          lineHeight: item.lineHeight,
          ...(maxLines ? { maxLines } : {}),
          letterSpacing: 0,
          color: visible(role(item.color), surface, item.text),
          align: item.align ?? 'left',
        }
        const transform = transformOf(item, opacityOf(item.opacity))
        built.push({
          z: item.z,
          items: dynamicField
            ? perScene((at, n) => ({
                id: `${item.id}-${n}`,
                type: 'text',
                ...at,
                text: '',
                style,
                transform,
                dynamic: { source: 'speaker' as const, field: dynamicField },
              }))
            : [
                {
                  id,
                  type: 'text',
                  ...timing,
                  text: text ?? '',
                  style,
                  transform,
                },
              ],
        })
        break
      }
      case 'captions': {
        const typography: TranscriptionStyle = {
          ...style.transcription,
          ...defaults.transcriptions[orientation],
          fontFamily: captionFontFamily,
          ...(item.captions.textTransform
            ? { textTransform: item.captions.textTransform }
            : {}),
          ...(item.captions.lineHeight !== undefined
            ? { lineHeight: item.captions.lineHeight }
            : {}),
          ...(item.captions.linesPerPage !== undefined
            ? { linesPerPage: item.captions.linesPerPage }
            : {}),
        }
        // v1's `Subtitles` block: a rounded line height times the lines.
        const captionHeight =
          Math.round(typography.fontSize * typography.lineHeight) *
          typography.linesPerPage
        const box = transformOf(item)
        const align = item.captions.align ?? 'center'
        const free = box.height - captionHeight
        const y =
          free <= 0
            ? Math.max(0, Math.min(box.y, height - captionHeight))
            : align === 'start'
              ? box.y
              : align === 'end'
                ? box.y + free
                : box.y + Math.round(free / 2)
        const captionStyle = {
          ...typography,
          color: visible(typography.color, surface, 'captions'),
        }
        built.push({
          z: item.z,
          items: perScene((at, n) => ({
            id: `${item.id}-${n}`,
            type: 'captions',
            ...at,
            style: captionStyle,
            transform: { ...box, y, height: captionHeight },
          })),
        })
        break
      }
      case 'component': {
        if (item.componentId === 'audio-visualizer') {
          const props = {
            ...item.props,
            color: visible(role(item.color), surface, 'waveform'),
          }
          const transform = transformOf(item)
          built.push({
            z: item.z,
            items: perScene((at, n) => ({
              id: `${item.id}-${n}`,
              type: 'component',
              componentId: 'audio-visualizer',
              ...at,
              props,
              transform,
            })),
          })
        } else if (item.componentId === 'picture') {
          const frame = {
            fit: item.props.fit,
            shape: item.props.shape,
            ...(item.props.radius ? { radius: item.props.radius } : {}),
            ...(item.props.borderColor
              ? { borderColor: role(item.props.borderColor) }
              : {}),
            ...(item.props.borderWidth
              ? { borderWidth: Math.round(item.props.borderWidth * width) }
              : {}),
          }
          if (item.props.source === 'speaker') {
            // Whoever is speaking, resolved at render time like the name:
            // one per scene, never dropped.
            const transform = transformOf(item, opacityOf(item.opacity))
            built.push({
              z: item.z,
              items: perScene((at, n) => ({
                id: `${item.id}-${n}`,
                type: 'component',
                componentId: 'picture',
                ...at,
                props: { source: 'speaker', ...frame },
                transform,
              })),
            })
            break
          }
          const url = episode.hostImageUrl
          if (!url) {
            if (!report.includes(REPORT_NO_HOST_IMAGE))
              report.push(REPORT_NO_HOST_IMAGE)
            break
          }
          built.push({
            z: item.z,
            items: [
              {
                id,
                type: 'component',
                componentId: 'picture',
                ...timing,
                props: { url, ...frame },
                transform: transformOf(item, opacityOf(item.opacity)),
              },
            ],
          })
        }
        break
      }
    }
  }

  // Track order is paint order: `tracks[0]` is on top. One track per
  // blueprint item: a follower's copies share it, since the scenes they
  // follow sit on one track and never overlap. A follower given no scene
  // makes no track.
  const tracks: TrackV3[] = []
  for (const { items } of built
    .map((entry, index) => ({ ...entry, index }))
    .sort((a, b) => b.z - a.z || a.index - b.index)) {
    if (!items.length) continue
    const base = templateBaseId(items[0]!.id)
    const id =
      base === 'captions-1'
        ? 'captions'
        : base === 'background-1'
          ? 'background'
          : `${base}-track`
    tracks.push({ id, items })
  }

  return { tracks, report }
}

type PerSceneTiming = { from: number; duration: number; follows: string }

/**
 * The id a layout item would have for the FIRST scene: `captions-3` →
 * `captions-1`, which is the id `templateRoles` and the pin tests know. An
 * upgrade's clone of a template follower (`captions-1-2`, `cloneId`) sheds
 * every ordinal too, so a switch still owns it. A user item's id (random
 * letters) has no ordinal and is its own base.
 */
export function templateBaseId(id: string): string {
  const match = /^(.+?)(?:-\d+)+$/.exec(id)
  return match ? `${match[1]}-1` : id
}

/** Whether `id` is one a layout owns, in any orientation, for any scene. */
export function isTemplateItemId(layout: Layout, id: string): boolean {
  return TEMPLATE_ITEM_IDS(layout).has(templateBaseId(id))
}

/**
 * The document's own tracks slotted into template tracks: picture tracks
 * (views, free media) where the media track always sat, above the background
 * and below everything else; the scenes track at the bottom (a scene draws
 * nothing, so its place in the paint order is moot).
 */
export function withMediaTrack(tracks: TrackV3[], media: TrackV3[]): TrackV3[] {
  const isScenes = (t: TrackV3) => t.items.some((i) => i.type === 'scene')
  const pictures = media.filter((t) => !isScenes(t))
  const scenes = media.filter(isScenes)
  const at = tracks.findIndex((t) => t.id === 'background')
  const placed =
    at === -1
      ? [...tracks, ...pictures]
      : [...tracks.slice(0, at), ...pictures, ...tracks.slice(at)]
  return [...placed, ...scenes]
}

/**
 * Ids the converter wrote before blueprints existed, plus the retired logo
 * badge (#546): a project that still carries a `logo-1` loses it the next
 * time a template is applied.
 */
export const CONVERTER_IDS = [
  'background-1',
  'visualizer-1',
  'title-1',
  'subtitle-1',
  'captions-1',
  'logo-1',
]

/** Every item id a layout's template can own, in any orientation. */
export function TEMPLATE_ITEM_IDS(layout: Layout): Set<string> {
  const ids = new Set(CONVERTER_IDS)
  for (const items of Object.values(BLUEPRINTS[layout].orientations)) {
    for (const item of items) ids.add(`${item.id}-1`)
  }
  return ids
}

/**
 * The part an element plays in a layout (#547), read from the blueprint, never
 * from the item's mutable content: a host picture carries only a URL, and a
 * picked URL clears an avatar's `source`. A layout switch re-applies the new
 * layout to the element of the same role; ids move between roles (Edgy's
 * `speaker-1` is the handle, Framed's the name).
 */
export type TemplateRole =
  | 'title'
  | 'subtitle'
  | 'captions'
  | 'visualizer'
  | 'host'
  | 'avatar'
  | 'speakerName'
  | 'speakerHandle'

/** An element's role, or `null` for a decorative piece (a card, a rule). */
export function roleOfBlueprintItem(item: BlueprintItem): TemplateRole | null {
  switch (item.type) {
    case 'text':
      switch (item.text) {
        case 'title':
        case 'subtitle':
          return item.text
        case 'speakerName':
          return 'speakerName'
        case 'speakerHandle':
          return 'speakerHandle'
        default:
          return null
      }
    case 'captions':
      return 'captions'
    case 'component':
      if (item.componentId === 'audio-visualizer') return 'visualizer'
      return item.props.source === 'speaker' ? 'avatar' : 'host'
    default:
      return null
  }
}

/** Item id → role for one layout in one orientation. */
export function templateRoles(
  layout: Layout,
  orientation: Orientation,
): Map<string, TemplateRole> {
  const roles = new Map<string, TemplateRole>()
  for (const item of BLUEPRINTS[layout].orientations[orientation]) {
    const role = roleOfBlueprintItem(item)
    if (role) roles.set(`${item.id}-1`, role)
  }
  return roles
}

/**
 * Template item ids that play no role (#547): backgrounds, cards, bars,
 * rules, a caption strip, a glyph. A switch always replaces them with the
 * new layout's, edited or not.
 */
export const DECORATIVE_IDS: ReadonlySet<string> = new Set(
  Object.values(BLUEPRINTS).flatMap((blueprint) =>
    Object.values(blueprint.orientations).flatMap((items) =>
      items
        .filter((item) => roleOfBlueprintItem(item) === null)
        .map((item) => `${item.id}-1`),
    ),
  ),
)
